/**
 * Texting-agent settings (texting-agent plan §7.3 — step 4, admin-gated by
 * the backend). Three sections:
 *
 *   GLOBAL — the switch stack shown honestly: the env kill switch is
 *   READ-ONLY here (only the owner flips CRM_AGENT_ENABLED), the tenant flag
 *   is a KILL-ONLY toggle. C-17: everything else the PUT whitelist accepts is
 *   editable here too — default mode, the money caps, pacing, conversation
 *   limits, batch defaults, and the opener age bands — and the conversation-
 *   hours line reads config, not a hardcoded literal.
 *
 *   TEMPLATES — the twelve REQUIRED approved lines live in the prompt
 *   registry; publishing one there IS the D-16 approval. §6.9b adds optional
 *   nudge variants (B–D) that rotate wording on the ladder — they never
 *   block readiness. This section links straight to their editors and shows
 *   the published/draft state.
 *
 *   PER STUDY — C-18: a summary grid of every study's agent state (enabled /
 *   mode / auto-engage / pivot / readiness) that click-loads the editor,
 *   above the existing single-study editor with the §8.3 readiness checklist
 *   rendered verbatim from the server (which enforces it — the UI cannot
 *   sneak a study live).
 *
 *   SCHEDULER MODE (docs/plans/crm-agent-scheduler-mode-plan.md B10; WP-10):
 *   the study's ROLE is a radio (Screening / Q&A + call / Scheduler; the first
 *   two are exactly the old Q&A checkbox off and on). A Scheduler study also
 *   shows SchedulerStudySettings (the owner's rules in plain words, the
 *   read-only recruiter list with its calendar aggregates and warnings, the
 *   address override, the 30 lines). `?study=<id>` opens a study directly (the
 *   setup checklist links here). Saves carry the row's updated_at, so a save
 *   racing another admin's gets 409 config_changed and reloads. Every save
 *   sends the WHOLE config with its change applied, and the Scheduler option
 *   shows only once the config read says `scheduler_supported`, because a
 *   deploy rolls this page before the api (WP-10 review, safety M1). A
 *   Scheduler study counts only the marks made while it is set to Scheduler,
 *   so a switch's preview checklist offers no "Mark done" (review M1).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Bot, CheckCircle2, Circle, ExternalLink, Loader2, Plus, ShieldOff, X } from 'lucide-react';
import api from '../../../services/api';
import PageHeader, { RefreshButton } from '../../components/PageHeader';
import { fieldCls } from '../../components/filters';
import { useConfirm, usePromptDialog } from '../../components/ui';
import ProfileKeyPicker from '../../components/forms/ProfileKeyPicker';
import SchedulerStudySettings, { type SchedulerReadinessDetail } from '../../components/scheduler/SchedulerStudySettings';
import {
  ROLE_OPTIONS, involvesScheduler, isRolePreview, roleChangePatch, roleOf, textingAgentSaveBody, visibleRoleOptions,
  type AgentRole,
} from '../../components/scheduler/schedulerLogic';

/**
 * C-17: compact numeric knob — drafts locally, commits on blur/Enter so a
 * half-typed number never fires a PUT. Admin-gated exactly like the existing
 * switches (the PUT 403s a member; the page shows its usual notice).
 */
function NumField({ id, label, value, onCommit, step = 1, min = 0, disabled, className = 'min-w-0' }: {
  id: string;
  label: string;
  value: number | null | undefined;
  onCommit: (n: number) => void;
  step?: number;
  min?: number;
  disabled?: boolean;
  className?: string;
}) {
  const [draft, setDraft] = useState<string>(value === null || value === undefined ? '' : String(value));
  useEffect(() => { setDraft(value === null || value === undefined ? '' : String(value)); }, [value]);
  const commit = () => {
    const n = step === 1 ? parseInt(draft, 10) : parseFloat(draft);
    if (!Number.isFinite(n) || n < min) {
      setDraft(value === null || value === undefined ? '' : String(value));
      return;
    }
    if (n !== value) onCommit(n);
  };
  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="block truncate text-xs font-medium text-gray-600 dark:text-gray-300" title={label}>{label}</label>
      )}
      <input
        id={id}
        type="number"
        inputMode="decimal"
        step={step}
        min={min}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className={`${fieldCls} ${label ? 'mt-0.5' : ''} w-full tabular-nums`}
      />
    </div>
  );
}

/**
 * §6.9b: one slot of the nudge ladder — drafts locally exactly like NumField
 * and commits on blur/Enter so a half-picked time never fires a PUT. The
 * commit hands back just this slot's value; the parent PUTs the FULL array
 * (the server sanitizes: strict HH:MM, dedupe, sort, cap 8).
 */
function TimeSlotField({ id, value, disabled, removeDisabled, onCommit, onRemove }: {
  id: string;
  value: string;
  disabled?: boolean;
  removeDisabled?: boolean;
  onCommit: (v: string) => void;
  onRemove: () => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const commit = () => {
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(draft)) { setDraft(value); return; }
    if (draft !== value) onCommit(draft);
  };
  return (
    <div className="inline-flex items-center gap-1">
      <input
        id={id}
        type="time"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className={`${fieldCls} tabular-nums`}
        aria-label={`Nudge time ${value}`}
      />
      <button
        type="button"
        disabled={disabled || removeDisabled}
        onClick={onRemove}
        aria-label={`Remove nudge time ${value}`}
        title={removeDisabled ? 'Keep at least one time of day' : 'Remove this time'}
        className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors duration-150 hover:bg-gray-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-red-400 sm:h-8 sm:w-8"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// C-17 knob catalogs — every key below is already accepted by the PUT
// whitelist (routes/crm.js pacing/limits objects) and resolved by
// crmAgent.resolveAgentSettings, so this section only surfaces what the API
// could always do.
const PACING_KNOBS: Array<{ key: string; label: string }> = [
  { key: 'baseMinSec', label: 'Base delay min (s)' },
  { key: 'baseMaxSec', label: 'Base delay max (s)' },
  { key: 'firstReplyMinSec', label: 'First reply min (s)' },
  { key: 'firstReplyMaxSec', label: 'First reply max (s)' },
  { key: 'clampMinSec', label: 'Never faster than (s)' },
  { key: 'clampMaxSec', label: 'Never slower than (s)' },
  { key: 'typingCharsPerSec', label: 'Typing speed (chars/s)' },
  { key: 'copilotApprovedDelaySecMin', label: 'Approved send min (s)' },
  { key: 'copilotApprovedDelaySecMax', label: 'Approved send max (s)' },
];
const LIMIT_KNOBS: Array<{ key: string; label: string }> = [
  { key: 'maxConcurrentConversations', label: 'Concurrent conversations' },
  { key: 'maxMessagesPerLeadPerDay', label: 'Messages / lead / day' },
  { key: 'maxTurnsPerConversation', label: 'Turns / conversation' },
  { key: 'conversationMaxAgeDays', label: 'Conversation max age (d)' },
  // maxNudges / nudgeAfterHours / dormantAfterHours live in NUDGE_KNOBS now —
  // the §6.9b Nudge-schedule card owns the whole ladder.
  { key: 'humanRecencyHours', label: 'Human-touch recency (h)' },
  { key: 'maxPivots', label: 'Pivots / conversation' },
  { key: 'maxDraftChars', label: 'Draft length cap (chars)' },
  // §6.7b human-first gate: how long a person gets to take the question before
  // the agent answers it itself. 0 = no wait (it answers immediately, and the
  // escalation is still raised).
  { key: 'unknownAnswerHumanWaitSec', label: 'Hold for a human — unknown answer (s)' },
  { key: 'botQuestionHumanWaitSec', label: 'Hold for a human — "is this a bot?" (s)' },
];
// §6.9b nudge ladder — the Nudge-schedule card's knobs. Keys live in
// settings.limits and commit with the same full-limits-object spread as
// LIMIT_KNOBS; maxNudges / nudgeAfterHours / dormantAfterHours moved here so
// the whole ladder — slot times + counters — sits in one place.
const NUDGE_KNOBS: Array<{ key: string; label: string }> = [
  { key: 'maxNudges', label: 'Nudges before final' },
  { key: 'nudgeAfterHours', label: 'First nudge after (h)' },
  { key: 'nudgeMinGapHours', label: 'Gap between nudges (h)' },
  { key: 'nudgeWeekendDaysRequired', label: 'Weekend days required' },
  { key: 'nudgeSlotGraceMinutes', label: 'Slot grace window (min)' },
  { key: 'dormantAfterHours', label: 'Dormant after final (h)' },
];
// §6.9b slot defaults — mirrors the server's fallback ladder; shown when the
// settings payload predates nudgeTimes (or carries an empty list).
const NUDGE_TIMES_DEFAULT = ['08:30', '11:00', '16:00', '18:30'];
const BATCH_KNOBS: Array<{ key: string; label: string }> = [
  { key: 'batchDefaultPacePerHour', label: 'Default pace (texts/hr)' },
  { key: 'batchReplyWindowDays', label: 'Reply window (d)' },
  { key: 'batchRecencyWarnDays', label: 'Recency warn (d)' },
  { key: 'batchRecencyHardStopDays', label: 'Recency hard stop (d)' },
  { key: 'batchMaxActive', label: 'Live batches (max)' },
];
// Wave-1 C-3 opener-age bands (limits.openerFreshMaxDays / openerAgedMinDays).
const OPENER_KNOBS: Array<{ key: string; label: string }> = [
  { key: 'openerFreshMaxDays', label: 'Fresh opener under (d)' },
  { key: 'openerAgedMinDays', label: 'Aged opener over (d)' },
];

// The TCPA conversation window the backend enforces (crmCompliance.js
// QUIET_START_HOUR/QUIET_END_HOUR). Used only when the settings payload
// carries no explicit window — the line reads config first, then these
// defaults-as-data (C-17: no hardcoded prose).
const QUIET_WINDOW_DEFAULT = { startHour: 8, endHour: 21 };
const fmtHour = (h: number) => `${h % 12 === 0 ? 12 : h % 12}${h >= 12 ? 'pm' : 'am'}`;

interface StudyRow { id: string; title: string; internal_title?: string | null; status?: string | null }
interface AgentConfig {
  study_id: string;
  enabled: boolean;
  mode: 'copilot' | 'auto';
  auto_engage: boolean;
  pivot_enabled: boolean;
  pivot_priority: number;
  config?: {
    texting_agent?: {
      offer_call_at_wrapup?: boolean;
      info_call_only?: boolean;
      // Scheduler mode (plan B10/B12).
      scheduler_only?: boolean;
      clinic_address?: string | null;
      owner_offer_tolerance_min?: number | null;
      /** This study's call length in minutes (10..240); unset = Booking hours' slot length. */
      call_length_min?: number | null;
    };
  } | null;
  /** The row's version: sent back with a save as its compare-and-swap. */
  updated_at?: string;
}
interface ReadinessItem { key: string; ok: boolean; blocking: boolean; detail: string }
interface Readiness { ready: boolean; items: ReadinessItem[]; scheduler?: SchedulerReadinessDetail }

const TEMPLATE_KEYS: Array<{ key: string; label: string; optional?: true }> = [
  { key: 'texting_agent.template.opening', label: 'Opening (new lead)' },
  { key: 'texting_agent.template.hold', label: '“Let me check with the team” hold' },
  { key: 'texting_agent.template.disclosure', label: 'Automation disclosure' },
  { key: 'texting_agent.template.wrapup', label: 'Wrap-up / handoff' },
  { key: 'texting_agent.template.nudge_1', label: 'Nudge' },
  { key: 'texting_agent.template.nudge_2', label: 'Final nudge' },
  // Required since §6.9c but missing from this list (scheduler plan B10).
  { key: 'texting_agent.template.form_nudge', label: 'Questionnaire nudge' },
  { key: 'texting_agent.template.pivot_transition', label: 'Pivot transition' },
  { key: 'texting_agent.template.distress_hold', label: 'Distress hold' },
  { key: 'texting_agent.template.call_offer', label: 'Call offer (O-9)' },
  { key: 'texting_agent.template.reengage_recent', label: 'Re-engage opener — recent (D-20)' },
  { key: 'texting_agent.template.reengage_aged', label: 'Re-engage opener — aged (D-20)' },
  { key: 'texting_agent.template.wrong_person_close', label: 'Wrong-number close (D-20)' },
  // §6.9b OPTIONAL nudge variants — extra wordings the ladder rotates through
  // so repeated nudges don't read copy-pasted. Never counted toward readiness
  // (the server's templates_published check ignores them).
  { key: 'texting_agent.template.nudge_variant_2', label: 'Nudge variant B (optional)', optional: true },
  { key: 'texting_agent.template.nudge_variant_3', label: 'Nudge variant C (optional)', optional: true },
  { key: 'texting_agent.template.nudge_variant_4', label: 'Nudge variant D (optional)', optional: true },
  // Bug 2026-08-03: the line that carries the texted questionnaire link when the
  // agent's own wording is unusable (it once sent the bare label “First Name”).
  { key: 'texting_agent.template.form_handoff', label: 'Questionnaire link handoff (optional)', optional: true },
];

interface PivotQuestion {
  id: string;
  profile_key: string;
  question_guidance: string | null;
  answer_type: 'boolean' | 'number' | 'choice';
  options: string[] | null;
  sort_order: number;
  active: boolean;
}

const READINESS_LABEL: Record<string, string> = {
  brief_published: 'Agent brief published',
  screener_lint: 'Screener lint clean',
  templates_published: 'All approved lines published',
  sign_off: 'Owner sign-off recorded',
  simulator_pack: 'Simulator pack reviewed',
  test_walkthrough: 'Override walkthrough done',
  auto_graduation: 'Graduated to auto (D-12)',
  // Scheduler mode (plan B10): four blocking items, the rest warnings.
  scheduler_lines_published: 'Scheduler lines published',
  scheduler_roster_live: 'Someone on the recruiter list can be booked',
  scheduler_timezone_valid: 'Timezone is valid',
  scheduler_prompt_ok: 'Scheduler prompt resolves',
  scheduler_closed_dates: 'Closed dates entered',
  scheduler_call_length: 'Call length fits the booking day',
  scheduler_clinic_address: 'Clinic address',
  scheduler_site_timezone: "Site timezone matches",
  scheduler_roster_activity: 'Recruiters use the CRM calendar',
  scheduler_call_number: '"What number" line',
  scheduler_welcome_night: 'Night welcome',
};

/** Where a failing scheduler readiness item is fixed. */
const SCHEDULER_ITEM_LINK: Record<string, { to: (studyId: string) => string; label: string }> = {
  scheduler_lines_published: { to: () => '/crm/settings/prompts', label: 'Open the prompt editor' },
  scheduler_roster_live: { to: (id) => `/crm/settings/studies/${id}?tab=recruiters`, label: "Open the study's Recruiters tab" },
  scheduler_closed_dates: { to: () => '/crm/settings/booking', label: 'Open Booking settings' },
  scheduler_call_length: { to: () => '/crm/settings/booking', label: 'Open Booking settings' },
  scheduler_clinic_address: { to: () => '/crm/settings/booking', label: 'Open Booking settings' },
};

export default function AgentSettings() {
  const prompt = usePromptDialog();
  const confirm = useConfirm();
  // ?study=<id> opens that study's editor (the setup checklist's "Open agent
  // settings" link lands here); picking a study keeps the URL in step.
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<any>(null);
  const [budget, setBudget] = useState<any>(null);
  const [studies, setStudies] = useState<StudyRow[]>([]);
  const [selected, setSelectedState] = useState<string>(() => searchParams.get('study') || '');
  const setSelected = (id: string) => {
    setSelectedState(id);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('study', id); else next.delete('study');
      return next;
    }, { replace: true });
  };
  const [config, setConfig] = useState<AgentConfig | null>(null);
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  // Does the api behind this page support the Scheduler role? (The config
  // read's flag; an older api during a deploy does not send it.)
  const [schedulerSupported, setSchedulerSupported] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // §7.3 pivot-question manager (owner curates the D-14 short screener).
  const [pivotQuestions, setPivotQuestions] = useState<PivotQuestion[]>([]);
  const [pivotDenied, setPivotDenied] = useState(false);
  const [pqKey, setPqKey] = useState('');
  const [pqType, setPqType] = useState<'boolean' | 'number' | 'choice'>('boolean');
  const [pqGuidance, setPqGuidance] = useState('');
  const [pqOptions, setPqOptions] = useState('');

  const fetchPivotQuestions = useCallback(async () => {
    try {
      const res = await api.get('/api/crm/agent/pivot-questions');
      setPivotQuestions(res.data?.questions || []);
      setPivotDenied(false);
    } catch (err: any) {
      // Members see the rest of the page; the manager itself is admin-only.
      if (err?.response?.status === 403) setPivotDenied(true);
      else console.error('[AgentSettings] pivot questions load failed:', err);
    }
  }, []);

  const addPivotQuestion = async () => {
    if (!pqKey) return;
    setBusy(true);
    setNotice(null);
    try {
      await api.post('/api/crm/agent/pivot-questions', {
        profile_key: pqKey,
        answer_type: pqType,
        question_guidance: pqGuidance.trim() || null,
        options: pqType === 'choice'
          ? pqOptions.split(',').map((s) => s.trim()).filter(Boolean)
          : null,
        sort_order: pivotQuestions.length,
      });
      setPqKey(''); setPqGuidance(''); setPqOptions('');
      await fetchPivotQuestions();
    } catch (err: any) {
      setNotice(err?.response?.data?.error === 'duplicate_profile_key'
        ? 'That profile key already has a pivot question — edit it below instead.'
        : err?.response?.data?.error === 'choice_needs_options'
          ? 'Choice questions need at least one option (comma-separated).'
          : 'Could not add the pivot question.');
    } finally {
      setBusy(false);
    }
  };

  const patchPivotQuestion = async (id: string, patch: Record<string, unknown>) => {
    setBusy(true);
    try {
      await api.patch(`/api/crm/agent/pivot-questions/${id}`, patch);
      await fetchPivotQuestions();
    } catch (err) {
      console.error('[AgentSettings] pivot question update failed:', err);
    } finally {
      setBusy(false);
    }
  };

  // L-10: per-template publish state ("Publishing IS the approval" needs the
  // page to SHOW which lines are published). Admin-gated route — members keep
  // the plain links.
  const [promptStates, setPromptStates] = useState<Map<string, boolean> | null>(null);

  // C-18: the per-study summary grid — every study's agent config at a
  // glance. Config reads are admin-only; a 403 hides the grid (members keep
  // the rest of the page), matching the pivot-manager pattern above.
  const [grid, setGrid] = useState<Map<string, { config: AgentConfig | null; ready: boolean | null }> | null>(null);
  const [gridDenied, setGridDenied] = useState(false);

  const fetchGrid = useCallback(async (list: StudyRow[]) => {
    if (!list.length) { setGrid(new Map()); return; }
    let denied = false;
    const entries = await Promise.all(list.map(async (s) => {
      try {
        const c = await api.get(`/api/crm/agent/studies/${s.id}/config`);
        const cfg: AgentConfig | null = c.data?.config || null;
        let ready: boolean | null = null;
        if (cfg) {
          // Readiness is computed server-side — only fetched for studies with
          // a config row (the agent candidates), so the page load stays light.
          try {
            const r = await api.get(`/api/crm/agent/studies/${s.id}/readiness`);
            ready = r.data?.ready === true;
          } catch { ready = null; }
        }
        return [s.id, { config: cfg, ready }] as const;
      } catch (err: any) {
        if (err?.response?.status === 403) denied = true;
        return [s.id, { config: null, ready: null }] as const;
      }
    }));
    if (denied) { setGridDenied(true); setGrid(null); return; }
    setGridDenied(false);
    setGrid(new Map(entries));
  }, []);

  const fetchGlobal = useCallback(async () => {
    try {
      const [s, st] = await Promise.all([
        api.get('/api/crm/agent/settings'),
        api.get('/api/crm/studies'),
      ]);
      setSettings(s.data?.settings || null);
      setBudget(s.data?.budget || null);
      const list: StudyRow[] = st.data?.studies || st.data || [];
      setStudies(list);
      fetchGrid(list); // fire-and-forget — the grid fills in as it loads
    } catch (err) {
      console.error('[AgentSettings] load failed:', err);
    } finally {
      setLoading(false);
    }
    try {
      const p = await api.get('/api/crm/prompts');
      setPromptStates(new Map((p.data?.prompts || []).map(
        (row: any) => [row.key, Boolean(row.customized && row.status === 'active')],
      )));
    } catch {
      setPromptStates(null); // member (403) or transient — badges just hide
    }
  }, [fetchGrid]);

  const fetchStudy = useCallback(async (studyId: string) => {
    if (!studyId) { setConfig(null); setReadiness(null); return; }
    try {
      const [c, r] = await Promise.all([
        api.get(`/api/crm/agent/studies/${studyId}/config`),
        api.get(`/api/crm/agent/studies/${studyId}/readiness`),
      ]);
      const cfg: AgentConfig = c.data?.config || {
        study_id: studyId, enabled: false, mode: 'copilot', auto_engage: false, pivot_enabled: false, pivot_priority: 0,
      };
      setConfig(cfg);
      setReadiness(r.data || null);
      setSchedulerSupported(c.data?.scheduler_supported === true);
      // Keep the C-18 grid row in lockstep with the editor (a toggle here must
      // not leave the summary stale).
      setGrid((prev) => {
        if (!prev) return prev;
        const next = new Map(prev);
        next.set(studyId, { config: c.data?.config || null, ready: r.data?.ready === true });
        return next;
      });
    } catch (err) {
      console.error('[AgentSettings] study load failed:', err);
    }
  }, []);

  useEffect(() => { fetchGlobal(); fetchPivotQuestions(); }, [fetchGlobal, fetchPivotQuestions]);
  useEffect(() => { fetchStudy(selected); }, [selected, fetchStudy]);

  const putTenant = async (patch: Record<string, unknown>) => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await api.put('/api/crm/agent/settings', patch);
      setSettings(res.data?.settings || settings);
      // A cap change lands on the budget payload, which the PUT does not
      // return — re-read so the displayed caps track what was saved.
      if (['per_conversation_cap_usd', 'daily_cap_usd', 'monthly_cap_usd'].some((k) => k in patch)) {
        try {
          const s = await api.get('/api/crm/agent/settings');
          setBudget(s.data?.budget || null);
        } catch { /* display-only refresh */ }
      }
    } catch (err) {
      console.error('[AgentSettings] tenant update failed:', err);
      setNotice('Could not save — are you a CRM admin?');
    } finally {
      setBusy(false);
    }
  };

  /** Save a per-study patch; resolves true when it saved. The row's updated_at
   *  rides along, so a save racing another admin's is refused (409
   *  config_changed) and the editor reloads instead of overwriting theirs. */
  const putStudy = async (patch: Record<string, unknown>, opts: { notReady?: string } = {}): Promise<boolean> => {
    if (!selected) return false;
    setBusy(true);
    setNotice(null);
    try {
      const res = await api.put(`/api/crm/agent/studies/${selected}/config`, {
        ...patch,
        ...(config?.updated_at ? { updated_at: config.updated_at } : {}),
      });
      setConfig(res.data?.config || config);
      await fetchStudy(selected);
      return true;
    } catch (err: any) {
      const code = err?.response?.data?.error;
      const readinessBack = err?.response?.data?.readiness;
      if (readinessBack) setReadiness(readinessBack);
      if (code === 'config_changed') {
        await fetchStudy(selected);
        setNotice("Someone else changed this study's agent settings a moment ago. They have been reloaded: make your change again.");
        return false;
      }
      setNotice(code === 'not_ready'
        ? opts.notReady || 'Not ready — every checklist item below must pass before the agent can be enabled.'
        : code === 'auto_not_graduated'
          ? 'Auto mode needs the D-12 graduation mark first.'
          : ['invalid_clinic_address', 'invalid_owner_offer_tolerance', 'invalid_call_length', 'invalid_scheduler_only'].includes(code)
            ? String(err?.response?.data?.detail || 'That value was not accepted.')
            : 'Could not save.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Save texting_agent keys: the whole stored config with the change applied
   * (schedulerLogic.textingAgentSaveBody: an older api during a deploy
   * replaces the config with what it is sent, so nothing may be left out).
   */
  const putTextingAgent = (keys: Record<string, unknown>, opts: { notReady?: string } = {}) =>
    putStudy(textingAgentSaveBody(config, keys), opts);

  /**
   * Change the study's role. A switch into or out of the Scheduler on a LIVE
   * study asks first (it must pass the new role's readiness, and only new
   * conversations take the new role); Screening and Q&A + call switch as the
   * old checkbox did.
   */
  const changeRole = async (from: AgentRole, to: AgentRole) => {
    if (!config || from === to) return;
    const intoScheduler = to === 'scheduler';
    if (config.enabled && involvesScheduler(from, to)) {
      const label = ROLE_OPTIONS.find((o) => o.role === to)?.label || to;
      // WP-12 review: an in-flight conversation keeps its session's stamp, so
      // one already with the assistant keeps it; one that was not never moves
      // to the assistant and runs under the Q&A + call walls, links stopped.
      const ok = await confirm({
        title: `Switch this live study to ${label}?`,
        message: intoScheduler
          ? "New conversations start with the scheduling assistant. Conversations already under way are not moved to it: they stay with the regular agent under the Q&A + call rules, and any link it would send is stopped. The switch is refused unless the Scheduler's readiness checklist passes, and it counts only the sign-off, simulator and walkthrough marks made while the study is set to Scheduler, so a live study usually has to be turned off, switched, and re-marked first."
          : "New conversations start in the new role; conversations already with the scheduling assistant keep it. The switch is refused unless the new role's readiness checklist passes.",
        confirmLabel: 'Switch role',
      });
      if (!ok) return;
    }
    await putTextingAgent(roleChangePatch(from, to), {
      notReady: intoScheduler
        ? "Not switched: the Scheduler's checklist below must pass first. Its sign-off, simulator and walkthrough marks count only when made while the study is set to Scheduler: untick auto-engage, turn the agent off for this study (its active conversations pause until someone resumes them), switch the role, mark them, then turn it back on. See Help, AI texting agent, section 17."
        : "Not switched: the new role's checklist below must pass first.",
    });
  };

  const markItem = async (item: string) => {
    if (!selected) return;
    // C-26: the live-walkthrough mark requires an evidence note (the backend
    // rejects note-less marks for this item only).
    let note: string | undefined;
    if (item === 'test_walkthrough') {
      // A Scheduler study's walkthrough is the owner's 2026-07-28 method (WP-12
      // review): a real conversation with a staff-owned test lead, never the
      // platform-wide CRM_SMS_OVERRIDE_TO redirect. Other studies read as before.
      const schedulerRole = roleOf(config?.config?.texting_agent) === 'scheduler';
      const entered = await prompt({
        title: 'Evidence note (required)',
        message: schedulerRole
          ? 'What is the walkthrough plan or result? (A real conversation with a staff-owned test lead, handed to the assistant with auto-engage off; never CRM_SMS_OVERRIDE_TO.)'
          : 'What did you walk through on the CRM_SMS_OVERRIDE_TO number?',
        confirmLabel: 'Mark done',
      });
      if (entered === null) return; // canceled
      note = entered.trim();
      if (!note) {
        setNotice('The walkthrough mark needs a short evidence note.');
        return;
      }
    }
    setBusy(true);
    try {
      await api.post(`/api/crm/agent/studies/${selected}/readiness`, { item, ...(note ? { note } : {}) });
      await fetchStudy(selected);
    } catch (err) {
      console.error('[AgentSettings] mark failed:', err);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
      </div>
    );
  }

  // Review M-16: each switch binds to its OWN field — the old derivation
  // read the RESOLVED flag as the env switch, so killing the network made the
  // platform row lie ("OFF") and greyed the kill checkbox (un-kill was
  // unreachable from the UI). Fallbacks keep an old cached payload rendering.
  const envOn = settings?.envEnabled ?? settings?.enabled === true;
  const tenantOn = settings?.tenantEnabled ?? settings?.enabled !== false;
  // The selected study's role (scheduler plan B10): Screening / Q&A + call /
  // Scheduler, read from the two config.texting_agent flags.
  const role: AgentRole = roleOf(config?.config?.texting_agent);
  // Is the checklist on screen another role's (a refused switch's)?
  const preview = isRolePreview(role, readiness);

  // §6.9b: the nudge slot ladder — HH:MM strings the server keeps sorted and
  // deduped; display ascending, fall back to the documented defaults when the
  // payload predates nudgeTimes. Every commit PUTs the FULL array.
  const nudgeTimes: string[] = Array.isArray(settings?.nudgeTimes) && settings.nudgeTimes.length > 0
    ? [...settings.nudgeTimes].sort()
    : NUDGE_TIMES_DEFAULT;
  const weekendDaysReq = settings?.limits?.nudgeWeekendDaysRequired ?? 2;
  const commitNudgeTimes = (next: string[]) => putTenant({ nudge_times: next });
  const addNudgeTime = () => {
    if (nudgeTimes.length >= 8) return;
    // Pick a free slot (the server dedupes, so never add one already there).
    const candidates = ['12:00', '09:30', '13:30', '15:00', '17:00', '19:30', '10:00', '14:00', '20:00'];
    commitNudgeTimes([...nudgeTimes, candidates.find((t) => !nudgeTimes.includes(t)) || '12:00']);
  };

  return (
    <div>
      <PageHeader
        title="Texting agent"
        icon={Bot}
        subtitle="The AI texting agent — switches, approved lines, and per-study readiness. Everything ships dark until every gate opens."
        actions={<RefreshButton onClick={() => { fetchGlobal(); if (selected) fetchStudy(selected); }} />}
      />

      {notice && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300" role="status">
          {notice}
        </div>
      )}

      {/* GLOBAL */}
      <section className="mt-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Global switches</h2>
        <div className="mt-3 flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {envOn ? (
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            ) : (
              <ShieldOff className="h-4 w-4 text-gray-400" />
            )}
            <span className="font-medium text-gray-900 dark:text-gray-100">Platform switch (CRM_AGENT_ENABLED)</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${envOn
              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}
            >
              {envOn ? 'ON' : 'OFF (dark)'}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">— environment-controlled; only the owner flips it.</span>
          </div>
          <label className="flex flex-wrap items-center gap-2 text-sm">
            <input
              type="checkbox"
              // The tenant flag is KILL-ONLY: unchecking it stops the agent
              // for this network even while the platform switch is on. Bound
              // to its OWN switch (M-16) so re-checking works after a kill —
              // and works while the platform is dark (it just stores intent).
              checked={tenantOn}
              disabled={busy}
              onChange={(e) => putTenant({ enabled: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
            />
            <span className="font-medium text-gray-900 dark:text-gray-100">Network kill switch</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              — unchecking pauses every conversation on this network.
              {envOn && !tenantOn ? ' Currently KILLED: the agent is off for this network.' : ''}
            </span>
          </label>
          {/* C-17: the knobs the PUT whitelist always accepted, surfaced.
              Everything commits on blur; a member's save 403s into the notice. */}
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="agent-default-mode" className="block text-xs font-medium text-gray-600 dark:text-gray-300">Default mode (new studies)</label>
              <select
                id="agent-default-mode"
                value={settings?.defaultMode === 'auto' ? 'auto' : 'copilot'}
                disabled={busy}
                onChange={(e) => putTenant({ default_mode: e.target.value })}
                className={`${fieldCls} mt-0.5`}
              >
                <option value="copilot">Copilot — approve every message</option>
                <option value="auto">Auto — send after validation</option>
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <NumField id="cap-convo" label="$ / conversation" value={budget?.perConversationCap} step={0.01} disabled={busy} onCommit={(n) => putTenant({ per_conversation_cap_usd: n })} />
              <NumField id="cap-daily" label="$ / day" value={budget?.dailyCap} step={0.01} disabled={busy} onCommit={(n) => putTenant({ daily_cap_usd: n })} />
              <NumField id="cap-monthly" label="$ / month" value={budget?.monthlyCap} step={0.01} disabled={busy} onCommit={(n) => putTenant({ monthly_cap_usd: n })} />
            </div>
            <span className="pb-1.5 text-xs text-gray-500 dark:text-gray-400">Spend caps fail closed — hitting one pauses the agent.</span>
          </div>

          {/* C-17: the hours line reads config (tenant window/timezone when the
              settings payload carries them, else the enforced compliance
              defaults) instead of asserting a literal. */}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Conversations {fmtHour(settings?.quietHours?.startHour ?? QUIET_WINDOW_DEFAULT.startHour)}–{fmtHour(settings?.quietHours?.endHour ?? QUIET_WINDOW_DEFAULT.endHour)}
            {settings?.timezone ? ` (${settings.timezone})` : ' (site time)'} — TCPA window; sends defer overnight.
          </p>

          <div>
            <h3 className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Pacing (§4.4 — seconds between replies)</h3>
            <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
              {PACING_KNOBS.map((k) => (
                <NumField
                  key={k.key}
                  id={`pacing-${k.key}`}
                  label={k.label}
                  value={settings?.pacing?.[k.key]}
                  disabled={busy}
                  onCommit={(n) => putTenant({ pacing: { ...(settings?.pacing || {}), [k.key]: n } })}
                />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Conversation limits (§6.9 / §6.12)</h3>
            <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
              {LIMIT_KNOBS.map((k) => (
                <NumField
                  key={k.key}
                  id={`limit-${k.key}`}
                  label={k.label}
                  value={settings?.limits?.[k.key]}
                  disabled={busy}
                  onCommit={(n) => putTenant({ limits: { ...(settings?.limits || {}), [k.key]: n } })}
                />
              ))}
            </div>
          </div>

          {/* §6.9b nudge schedule — the editable slot ladder + its knobs. */}
          <div>
            <h3 className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Nudge schedule (§6.9b)</h3>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {nudgeTimes.map((t, i) => (
                <TimeSlotField
                  key={`${t}-${i}`}
                  id={`nudge-time-${i}`}
                  value={t}
                  disabled={busy}
                  removeDisabled={nudgeTimes.length <= 1}
                  onCommit={(v) => commitNudgeTimes(nudgeTimes.map((x, j) => (j === i ? v : x)))}
                  onRemove={() => commitNudgeTimes(nudgeTimes.filter((_, j) => j !== i))}
                />
              ))}
              <button
                type="button"
                disabled={busy || nudgeTimes.length >= 8}
                onClick={addNudgeTime}
                title={nudgeTimes.length >= 8 ? 'Up to 8 times of day' : 'Add a time of day'}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                <Plus className="h-3.5 w-3.5" /> Add time
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Times of day nudges aim for, in the tenant timezone{settings?.timezone ? ` (${settings.timezone})` : ''} — up to 8, kept sorted and deduped by the server. Nudge N targets slot N, wrapping around.
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
              {NUDGE_KNOBS.map((k) => (
                <NumField
                  key={k.key}
                  id={`limit-${k.key}`}
                  label={k.label}
                  value={settings?.limits?.[k.key]}
                  disabled={busy}
                  onCommit={(n) => putTenant({ limits: { ...(settings?.limits || {}), [k.key]: n } })}
                />
              ))}
            </div>
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              Default cadence: one follow-up later the same day the lead last texted us (the single 6pm slot), then one per week at that time — the min-gap (150h ≈ 6¼ days) is what makes “next open slot” land a week out. A reply re-anchors the ladder to same-day. The final nudge goes out once the budget is spent{weekendDaysReq > 0 ? ` and ${weekendDaysReq} weekend day${weekendDaysReq === 1 ? '' : 's'} carried a nudge` : ''} — and if coverage can’t complete, it still fires after the longer of the dormancy window and the min-gap, so no conversation closes without it and the fallback never undercuts the weekly spacing.
            </p>
          </div>

          <div>
            <h3 className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Batch defaults (D-21 / D-22) + opener age bands</h3>
            <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
              {[...BATCH_KNOBS, ...OPENER_KNOBS].map((k) => (
                <NumField
                  key={k.key}
                  id={`limit-${k.key}`}
                  label={k.label}
                  value={settings?.limits?.[k.key]}
                  disabled={busy}
                  onCommit={(n) => putTenant({ limits: { ...(settings?.limits || {}), [k.key]: n } })}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TEMPLATES */}
      <section className="mt-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Approved lines (D-16 + D-20)</h2>
          <span className="text-xs text-gray-500 dark:text-gray-400">Publishing a line in its editor IS the approval; readiness requires every line not marked optional — the optional nudge variants only rotate wording and never block readiness. A Scheduler study's own lines are listed on its settings below.</span>
        </div>
        <ul className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {TEMPLATE_KEYS.map((t) => {
            // L-10: the publish-state badge this section always claimed to
            // show — published = the owner's approval exists (D-16).
            const published = promptStates?.get(t.key);
            return (
              <li key={t.key}>
                <Link
                  to={`/crm/settings/prompts/${encodeURIComponent(t.key)}`}
                  className="inline-flex min-h-[44px] w-full items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 transition-colors duration-150 hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                >
                  <span className="min-w-0 truncate">{t.label}</span>
                  <span className="inline-flex flex-shrink-0 items-center gap-1.5">
                    {promptStates && (
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${published
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'}`}
                      >
                        {published ? 'Published' : 'Draft'}
                      </span>
                    )}
                    <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      {/* PIVOT QUESTIONS (§7.3 / D-14) */}
      {!pivotDenied && (
        <section className="mt-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Pivot questions (D-14)</h2>
            <span className="text-xs text-gray-500 dark:text-gray-400">The short neutral screener the agent asks before pivoting a lead — 5–8 high-level questions, keyed by field tag.</span>
          </div>

          {pivotQuestions.length > 0 && (
            <ul className="mt-3 flex flex-col gap-1.5">
              {pivotQuestions.map((q) => (
                <li key={q.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm dark:border-gray-700">
                  <span className={`font-medium ${q.active ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 line-through'}`}>{q.profile_key}</span>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">{q.answer_type}</span>
                  {q.answer_type === 'choice' && q.options?.length ? (
                    <span className="max-w-[260px] truncate text-xs text-gray-400" title={q.options.join(', ')}>{q.options.join(' · ')}</span>
                  ) : null}
                  {q.question_guidance && (
                    <span className="max-w-[320px] truncate text-xs text-gray-500 dark:text-gray-400" title={q.question_guidance}>{q.question_guidance}</span>
                  )}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => patchPivotQuestion(q.id, { active: !q.active })}
                    className="ml-auto inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                  >
                    {q.active ? 'Deactivate' : 'Reactivate'}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {pivotQuestions.length === 0 && (
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              None yet — the pivot probe skips anything it already knows from screening, so these only cover the gaps (D-14: the owner curates ~5–8).
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-end gap-2">
            <div className="min-w-[220px] flex-1">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Field tag (profile key)</label>
              <ProfileKeyPicker value={pqKey} onChange={(k) => setPqKey(k || '')} inputClassName={fieldCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300" htmlFor="pq-type">Answer type</label>
              <select id="pq-type" value={pqType} onChange={(e) => setPqType(e.target.value as any)} className={fieldCls}>
                <option value="boolean">Yes / no</option>
                <option value="number">Number</option>
                <option value="choice">Choice</option>
              </select>
            </div>
            {pqType === 'choice' && (
              <div className="min-w-[200px]">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300" htmlFor="pq-options">Options (comma-separated)</label>
                <input id="pq-options" value={pqOptions} onChange={(e) => setPqOptions(e.target.value)} className={fieldCls} placeholder="never, former, current" />
              </div>
            )}
            <div className="min-w-[220px] flex-1">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300" htmlFor="pq-guidance">Phrasing guidance (optional)</label>
              <input id="pq-guidance" value={pqGuidance} onChange={(e) => setPqGuidance(e.target.value)} className={fieldCls} placeholder="e.g. ask casually whether they use any tobacco" maxLength={500} />
            </div>
            <button
              type="button"
              disabled={busy || !pqKey}
              onClick={addPivotQuestion}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
            >
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
        </section>
      )}

      {/* PER STUDY */}
      <section className="mt-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Per-study enablement</h2>

        {/* C-18: every study's agent state at a glance (§7.3's summary grid).
            Clicking a row loads it in the editor below. Admin-only reads — a
            member 403 hides the grid, never the section. */}
        {!gridDenied && grid && studies.length > 0 && (
          <div className="mt-3 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Study</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Agent</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Mode</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Auto-engage</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Pivot</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Ready</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {[...studies]
                    .sort((a, b) => {
                      const ca = grid.get(a.id)?.config; const cb = grid.get(b.id)?.config;
                      // Enabled first, then configured, then alphabetical.
                      const rank = (c: AgentConfig | null | undefined) => (c?.enabled ? 0 : c ? 1 : 2);
                      return rank(ca) - rank(cb)
                        || String(a.internal_title || a.title).localeCompare(String(b.internal_title || b.title));
                    })
                    .map((s) => {
                      const row = grid.get(s.id);
                      const cfg = row?.config || null;
                      return (
                        <tr
                          key={s.id}
                          onClick={() => setSelected(s.id)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(s.id); } }}
                          tabIndex={0}
                          role="button"
                          aria-label={`Edit agent config for ${s.internal_title || s.title}`}
                          className={`cursor-pointer transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 ${
                            selected === s.id ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`}
                        >
                          <td className="max-w-[280px] truncate px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">{s.internal_title || s.title}</td>
                          <td className="px-3 py-2 text-sm">
                            {cfg?.enabled ? (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300"><span className="h-1.5 w-1.5 rounded-full bg-current" />On</span>
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-500">{cfg ? 'Off' : '—'}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                            {cfg ? (cfg.mode === 'auto' ? 'Auto' : 'Copilot') : '—'}
                            {cfg?.config?.texting_agent?.scheduler_only === true ? (
                              <span className="ml-1.5 rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">Scheduler</span>
                            ) : cfg?.config?.texting_agent?.info_call_only === true && (
                              <span className="ml-1.5 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">Q&A + call only</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">{cfg ? (cfg.auto_engage ? 'Yes' : 'No') : '—'}</td>
                          <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                            {cfg?.pivot_enabled ? `Yes · p${cfg.pivot_priority ?? 0}` : cfg ? 'No' : '—'}
                          </td>
                          <td className="px-3 py-2 text-sm">
                            {row?.ready === true && <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700 dark:bg-green-900/40 dark:text-green-300">Ready</span>}
                            {row?.ready === false && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Not ready</span>}
                            {(row?.ready === null || row?.ready === undefined) && <span className="text-xs text-gray-400 dark:text-gray-500">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label htmlFor="agent-study" className="text-sm text-gray-600 dark:text-gray-300">Study</label>
          <select
            id="agent-study"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className={fieldCls}
          >
            <option value="">Choose a study…</option>
            {studies.map((s) => (
              <option key={s.id} value={s.id}>{s.internal_title || s.title}</option>
            ))}
          </select>
        </div>

        {selected && config && (
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  disabled={busy || (!config.enabled && !(readiness?.ready))}
                  onChange={(e) => putStudy({ enabled: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                />
                <span className="font-medium text-gray-900 dark:text-gray-100">Agent enabled for this study</span>
                {!config.enabled && !readiness?.ready && (
                  <span className="text-xs text-amber-600 dark:text-amber-400">blocked by the checklist →</span>
                )}
              </label>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-300">Mode</span>
                <select
                  value={config.mode}
                  disabled={busy}
                  onChange={(e) => putStudy({ mode: e.target.value })}
                  className={fieldCls}
                  aria-label="Conversation mode"
                >
                  <option value="copilot">Copilot — approve every message</option>
                  <option value="auto">Auto — send after validation</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={config.auto_engage}
                  disabled={busy}
                  onChange={(e) => putStudy({ auto_engage: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                />
                <span className="text-gray-900 dark:text-gray-100">Auto-engage fresh leads (O-8)</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={config.pivot_enabled}
                  // A Scheduler study never pivots (the server keeps it off).
                  disabled={busy || role === 'scheduler'}
                  onChange={(e) => putStudy({ pivot_enabled: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                />
                <span className="text-gray-900 dark:text-gray-100">Eligible as a pivot target (step 6)</span>
                {role === 'scheduler' && <span className="text-xs text-gray-500 dark:text-gray-400">— not used by the Scheduler role</span>}
              </label>
              {config.pivot_enabled && (
                // C-17: pivot_priority had no input anywhere — the PUT always
                // accepted it. Higher wins when several enabled studies fit.
                <div className="flex items-center gap-2 pl-6 text-sm">
                  <label htmlFor="pivot-priority" className="text-gray-600 dark:text-gray-300">Pivot priority</label>
                  <NumField
                    id="pivot-priority"
                    label=""
                    className="w-20"
                    value={config.pivot_priority ?? 0}
                    disabled={busy}
                    onCommit={(n) => putStudy({ pivot_priority: n })}
                  />
                  <span className="text-xs text-gray-500 dark:text-gray-400">— higher wins when several studies fit a lead.</span>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  // D-23 (review M-20): the wrap-up call offer, per study,
                  // default OFF — on, the approved call-offer line + a fresh
                  // booking link join the wrap-up text. (The Scheduler role
                  // never wraps up with a link, so it is not used there.)
                  checked={config.config?.texting_agent?.offer_call_at_wrapup === true}
                  disabled={busy || role === 'scheduler'}
                  onChange={(e) => putStudy({
                    config: {
                      ...(config.config || {}),
                      texting_agent: {
                        ...(config.config?.texting_agent || {}),
                        offer_call_at_wrapup: e.target.checked,
                      },
                    },
                  })}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                />
                <span className="text-gray-900 dark:text-gray-100">Offer a call at wrap-up (D-23)</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">— appends the approved call-offer line + booking link.</span>
              </label>
              {/* The study's ROLE (scheduler plan B10 SETUP PROCESS). Screening
                  and Q&A + call are the old "Q&A + call scheduling only"
                  checkbox off and on (owner toggle 2026-08-13: the agent
                  answers study questions from the brief and sends the call
                  link; no prescreening, no online form, no screener needed);
                  Scheduler is the texting agent's scheduling assistant. */}
              <fieldset className="text-sm">
                <legend className="text-gray-600 dark:text-gray-300">Role</legend>
                <div className="mt-1 flex flex-col gap-1.5">
                  {visibleRoleOptions(schedulerSupported, role).map((o) => (
                    <label key={o.role} className="flex items-start gap-2">
                      <input
                        type="radio"
                        name="agent-role"
                        value={o.role}
                        checked={role === o.role}
                        disabled={busy}
                        onChange={() => changeRole(role, o.role)}
                        className="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                      />
                      <span className="min-w-0">
                        <span className="text-gray-900 dark:text-gray-100">{o.label}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">{o.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>

            <div>
              <h3 className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Readiness checklist (§8.3)</h3>
              {preview && (
                // A refused role switch shows the NEW role's checklist. A mark
                // made now is stamped with the saved role, so none is offered.
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                  This is the checklist for the role you picked; the study is still saved as{' '}
                  {ROLE_OPTIONS.find((o) => o.role === role)?.label || role}.
                  {readiness?.scheduler
                    ? ' Marks count for the Scheduler only when made while the study is set to Scheduler.'
                    : ''}
                </p>
              )}
              <ul className="mt-2 flex flex-col gap-1.5">
                {(readiness?.items || []).map((i) => (
                  <li key={i.key} className="flex items-start gap-2 text-sm">
                    {i.ok ? (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600 dark:text-green-400" />
                    ) : (
                      <Circle className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-300 dark:text-gray-600" />
                    )}
                    <span className="min-w-0">
                      <span className={`font-medium ${i.ok ? 'text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-300'}`}>
                        {READINESS_LABEL[i.key] || i.key}
                      </span>
                      {!i.blocking && (
                        <span className="ml-1 text-xs text-gray-400">
                          {i.key === 'auto_graduation' ? '(auto mode only)' : '(informational)'}
                        </span>
                      )}
                      {/* A scheduler item's detail names people and lines, so it
                          wraps instead of truncating. */}
                      <span
                        className={`block text-xs text-gray-500 dark:text-gray-400 ${i.key.startsWith('scheduler_') ? 'break-words' : 'truncate'}`}
                        title={i.detail}
                      >
                        {i.detail}
                      </span>
                      {!i.ok && SCHEDULER_ITEM_LINK[i.key] && (
                        <Link
                          to={SCHEDULER_ITEM_LINK[i.key].to(selected)}
                          className="mt-0.5 inline-block text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
                        >
                          {SCHEDULER_ITEM_LINK[i.key].label}
                        </Link>
                      )}
                      {['brief_published', 'screener_lint'].includes(i.key) && (
                        <Link
                          to={`/crm/settings/studies/${selected}?tab=${i.key === 'brief_published' ? 'agent' : 'prescreen'}`}
                          className="mt-0.5 inline-block text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
                        >
                          {i.key === 'brief_published' ? 'Open the Agent brief tab' : 'Open the Prescreen tab'}
                        </Link>
                      )}
                      {!i.ok && !preview && ['sign_off', 'simulator_pack', 'test_walkthrough', 'auto_graduation'].includes(i.key) && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => markItem(i.key)}
                          className="mt-1 inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                        >
                          Mark done (records you + now)
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* The Scheduler role's own settings (scheduler plan B10). */}
        {selected && config && role === 'scheduler' && (
          <SchedulerStudySettings
            studyId={selected}
            textingAgent={config.config?.texting_agent || {}}
            scheduler={readiness?.scheduler}
            busy={busy}
            onSaveTextingAgent={putTextingAgent}
          />
        )}
      </section>
    </div>
  );
}
